import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Anchor,
  Button,
  Divider,
  Image,
  NumberFormatter,
  Pagination,
  Select,
  Skeleton,
  Table,
  Tabs
} from '@mantine/core';
import { AlertCircle, ArrowLeft, Minus, Plus, Trash } from 'tabler-icons-react';
import PropTypes from 'prop-types';
import AppStack from '../../common/AppStack';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as HelperContext } from '../../../providers/HelperContextProvider';
import ProductImageShowcase from '../products/ProductImageShowcase';
import {
  CANADA_PROVINCE_LIST_OPTIONS,
  LOCATION_LIST_OPTIONS
} from '../../../config/locationConstants';
import { useVendorCatalogCheckout } from '../../../helpers/vendorCatalogCheckoutHelper';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import VendorCatalogResourceUnavailable from './VendorCatalogResourceUnavailable';
import ProductVariantInputPreview from '../products/ProductVariantInputPreview';
import { useCatalogBackPath } from '../../../helpers/catalogHelper';
import { EXTERNAL_CONNECTION_CONFIGS } from '../external/config/externalConnectionConfigs';
import { getEcomVendorProductExternalConnectionConfigs } from '../../../helpers/vendorHelper';
import { CURRENCY_TYPE_LIST } from '../../../config/constants';
import AppTextEditorContent from '../../common/AppTextEditorContent';
import AppImage from '../../common/AppImage';
import { singularPluralFormat } from '../../../helpers/format';

const PRODUCT_TABS_ENUM = {
  DESCRIPTION: 'DESCRIPTION',
  DETAILS: 'DETAILS',
  VARIANTS: 'VARIANTS',
  SHIPPING: 'SHIPPING'
};

const PRODUCT_TABS_LIST = [
  { label: 'Product description', value: PRODUCT_TABS_ENUM.DESCRIPTION },
  {
    label: 'Product details',
    value: PRODUCT_TABS_ENUM.DETAILS,
    isVisible: ({ isBundleProduct }) => !isBundleProduct
  },
  {
    label: 'Variants',
    value: PRODUCT_TABS_ENUM.VARIANTS,
    isVisible: ({ isBundleProduct }) => !isBundleProduct
  },
  { label: 'Shipping Information', value: PRODUCT_TABS_ENUM.SHIPPING }
];

const MAX_PAGE_LENGTH = 12;

const VendorCatalogProductView = ({ vendorPreview, baseUrl }) => {
  const { search } = useLocation();
  const fromQuery = new URLSearchParams(search).get('query');
  const { ecomVendor, urlHandle } = useParams();
  const {
    state: helperState,
    setVendorCatalogRemoveStoreProductModal
  } = useContext(HelperContext);
  const { state, fetchStoreCatalogProduct } = useContext(StoreContext);
  const { state: authState } = useContext(AuthContext);
  const {
    cart: catalogCart,
    addProducts,
    removeProducts
  } = useVendorCatalogCheckout(authState.pkEcomStore);
  const { isTabletOrSmaller } = useMediaQueryIndex();
  const [showNotFound, setShowNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productFilterState, setProductFilterState] = useState({
    variantOptionsSelected: {},
    selectedImageIndex: 0,
    variantPageIndex: 1
  });
  const [activeTab, setActiveTab] = useState(PRODUCT_TABS_ENUM.DESCRIPTION);
  const { backPath } = useCatalogBackPath(baseUrl);
  const vendor = state.catalogVendors.value.find(
    (v) => v.urlHandle === ecomVendor
  );
  const vendorProduct =
    state.catalogProduct.value?.urlHandle === urlHandle
      ? state.catalogProduct.value
      : null;
  const vendorProvinces = vendorProduct?.ecomVendor.ecomVendorProvinces
    .map((p) =>
      LOCATION_LIST_OPTIONS.find((f) => f.value.toString() === p.toString())
    )
    .filter((p) => p)
    .sort((a, b) => a.label.localeCompare(b.label));
  const variantOptions =
    vendorProduct?.ecomVendorProductVariants.reduce((r, c) => {
      c.ecomVendorProductVariantOptions.forEach((op) => {
        const existingOption = r.find((f) => f.name === op.name);
        if (existingOption) {
          if (!existingOption.values.includes(op.value)) {
            existingOption.values.push(op.value);
          }
        }
        else {
          r.push({ name: op.name, values: [op.value] });
        }
      });
      return r;
    }, []) ?? [];

  const selectedVariant = vendorProduct?.ecomVendorProductVariants.find((v) =>
    Object.entries(
      productFilterState.variantOptionsSelected
    ).every(([key, value]) =>
      v.ecomVendorProductVariantOptions.find(
        (f) => f.name === key && f.value === value
      )
    )
  );
  const variantPrice = selectedVariant?.price ?? 0;
  const markupPrice = variantPrice * 1.3;
  const isSelected =
    vendorProduct &&
    catalogCart.ecomVendorProducts.some((p) => p.uuid === vendorProduct.uuid);

  const productAlreadyInStore =
    vendorProduct &&
    state.ecomStoreProducts.value.some(
      (p) => p.ecomVendorProduct.uuid === vendorProduct.uuid
    );

  const isDemoMode =
    authState.userData.isAdmin && helperState.vendorCatalog.isDemoMode;
  const isWrongViewMode =
    vendorProduct && vendorProduct.ecomVendor.isDemoVendor !== isDemoMode;

  const productCurrencyType = CURRENCY_TYPE_LIST.find(
    (c) =>
      c.value.toString() ===
      (vendorProduct?.fkCurrencyTypeOverride?.toString() ||
        vendor?.fkCurrencyType?.toString())
  );

  useEffect(() => {
    if (vendorProduct && !productFilterState.variantOption1) {
      setProductFilterState({
        ...productFilterState,
        variantPageIndex: 1,
        variantOptionsSelected: variantOptions.reduce(
          (r, c) => ({ ...r, [c.name]: c.values[0] }),
          {}
        )
      });
    }
  }, [vendorProduct]);

  useEffect(() => {
    if (vendor && urlHandle) {
      setShowNotFound(false);
      fetchStoreCatalogProduct(
        {
          pkEcomStore: vendorPreview ? null : authState.pkEcomStore,
          pkEcomVendor: vendorPreview ? authState.pkEcomVendor : null
        },
        {
          fkEcomVendor: vendor.pkEcomVendor,
          urlHandle
        },
        null,
        () => setShowNotFound(true)
      );
    }
  }, [vendor, urlHandle]);

  return isWrongViewMode || showNotFound ? (
    <AppStack
      style={{
        flex: 1,
        width: '100%',
        maxWidth: 1600,
        alignSelf: 'center',
        gap: 0
      }}
    >
      <AppFlexbox style={{ padding: '16px 0px' }}>
        <Button
          color="dark"
          component={Link}
          leftSection={<ArrowLeft size={18} />}
          to={backPath}
        >
          Back to search
        </Button>
      </AppFlexbox>
      <VendorCatalogResourceUnavailable notFound={showNotFound} />
    </AppStack>
  ) : vendorProduct ? (
    <AppStack
      style={{
        flex: 1,
        width: '100%',
        maxWidth: 1600,
        alignSelf: 'center',
        gap: 0
      }}
    >
      <AppFlexbox style={{ padding: '16px 0px' }}>
        <Button
          color="dark"
          component={Link}
          leftSection={<ArrowLeft size={18} />}
          to={backPath}
        >
          Back to search
        </Button>
      </AppFlexbox>
      <AppCard radius="md" shadow="xl" style={{ minHeight: 600 }} withBorder>
        <AppFlexbox
          style={{
            flex: 1,
            flexDirection: isTabletOrSmaller ? 'column' : 'row'
          }}
        >
          <ProductImageShowcase
            images={
              vendorProduct.ecomVendorBundle &&
              vendorProduct.ecomVendorProductMedia.length === 0
                ? vendorProduct.bundleCatalogProducts
                    .map(
                      (p) =>
                        p.ecomVendorProductMedia.sort(
                          (a, b) => a.sort - b.sort
                        )[0]
                    )
                    .filter((m) => m)
                : vendorProduct.ecomVendorProductMedia
            }
            maxWidth={isTabletOrSmaller ? 'unset' : 600}
            productAlreadyInStore={productAlreadyInStore}
          />
          <AppStack style={{ flex: 1 }}>
            <AppStack style={{ gap: 0, alignItems: 'start' }}>
              <AppText style={{ fontSize: 28, fontWeight: 700 }}>
                {vendorProduct.name}
              </AppText>
              {vendorProduct.minimumQuantity && (
                <AppText style={{ fontSize: 14, color: '#eca70a' }}>
                  Minimum buy count of {vendorProduct.minimumQuantity}
                </AppText>
              )}

              <AppText style={{ fontSize: 16 }}>
                by{' '}
                <Anchor
                  c="dodgerblue"
                  component={Link}
                  style={{ fontSize: 16, fontWeight: 500 }}
                  to={`${baseUrl}/vendors/${
                    vendorProduct.ecomVendor.urlHandle
                  }?from=vendor-product&fromResource=${urlHandle}${
                    fromQuery ? `&query=${encodeURIComponent(fromQuery)}` : ''
                  }`}
                >
                  {vendorProduct.ecomVendor.name}
                </Anchor>
              </AppText>

              {productAlreadyInStore ? (
                <Button
                  color="red"
                  disabled={vendorPreview}
                  leftSection={<Trash size={18} />}
                  onClick={() => {
                    setVendorCatalogRemoveStoreProductModal({
                      isOpen: true,
                      ecomVendorProducts: [vendorProduct]
                    });
                  }}
                  style={{ marginTop: 10 }}
                  variant="filled"
                >
                  Remove from store
                </Button>
              ) : isSelected ? (
                <Button
                  color="red"
                  disabled={vendorPreview}
                  leftSection={<Minus size={18} />}
                  onClick={() => {
                    removeProducts([vendorProduct.uuid]);
                  }}
                  style={{ marginTop: 10 }}
                  variant="outline"
                >
                  Unselect product
                </Button>
              ) : (
                <Button
                  color="blue"
                  disabled={vendorPreview}
                  leftSection={<Plus size={18} />}
                  loading={loading}
                  onClick={() => {
                    setLoading(true);
                    addProducts(
                      [vendorProduct.uuid],
                      () => setLoading(false),
                      () => setLoading(false)
                    );
                  }}
                  style={{ marginTop: 10 }}
                  variant="filled"
                >
                  Add product to your store
                </Button>
              )}
            </AppStack>
            <Divider />

            <AppFlexbox>
              <AppStack style={{ flex: 1, gap: 0 }}>
                <AppText style={{ fontSize: 16 }}>Price</AppText>
                <AppText style={{ fontSize: 24 }}>
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontWeight: 700 }}
                    suffix={` ${productCurrencyType?.abbr || ''}`}
                    thousandSeparator
                    value={variantPrice / 100}
                  />
                </AppText>
              </AppStack>
              <AppStack style={{ flex: 1, gap: 0 }}>
                <AppText style={{ fontSize: 16 }}>Profit</AppText>
                <AppText style={{ fontSize: 24 }}>
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontWeight: 700 }}
                    suffix={` ${productCurrencyType?.abbr || ''}`}
                    thousandSeparator
                    value={(markupPrice - variantPrice) / 100}
                  />
                </AppText>
              </AppStack>
              <AppStack style={{ flex: 1, gap: 0 }}>
                <AppText style={{ fontSize: 16 }}>Retail price</AppText>
                <AppText style={{ fontSize: 24 }}>
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    style={{ fontWeight: 700 }}
                    suffix={` ${productCurrencyType?.abbr || ''}`}
                    thousandSeparator
                    value={markupPrice / 100}
                  />
                </AppText>
                <AppText style={{ fontSize: 14, color: '#999' }}>
                  at 30% markup
                </AppText>
              </AppStack>
            </AppFlexbox>
            <Divider />

            {vendorProduct.ecomVendorBundle ? (
              <AppStack style={{}}>
                <AppCard
                  radius={isTabletOrSmaller ? 0 : 'md'}
                  shadow="xs"
                  style={{ padding: 0 }}
                  withBorder
                >
                  <AppFlexbox
                    style={{
                      padding: 8,
                      gap: 8,
                      backgroundColor: 'dodgerblue',
                      color: '#fff',
                      alignItems: 'center'
                    }}
                  >
                    <AppStack>
                      <AlertCircle size={18} />
                    </AppStack>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      Product bundle
                    </AppText>
                  </AppFlexbox>
                  <AppStack style={{ padding: 8, gap: 5 }}>
                    <AppText style={{ fontSize: 14 }}>
                      This is a bundle that sells multiple products together.
                    </AppText>
                    <AppText
                      style={{
                        fontSize: 14,
                        fontStyle: 'italic',
                        color: '#666'
                      }}
                    >
                      Each item included in this bundle will be added to your
                      store separately.
                    </AppText>
                  </AppStack>
                </AppCard>

                <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                  Bundle products
                </AppText>

                {vendorProduct.bundleCatalogProducts?.map((p, index) => {
                  const productImage = p.ecomVendorProductMedia.sort(
                    (a, b) => a.sort - b.sort
                  )[0];

                  return (
                    <React.Fragment key={p.pkEcomVendorProduct}>
                      {index > 0 && <Divider />}
                      <AppStack style={{ gap: 0 }}>
                        <AppFlexbox>
                          <AppFlexbox
                            component={Link}
                            style={{
                              color: '#000',
                              textDecoration: 'none'
                            }}
                            to={`/merchant/products/discover/products/${p.vendorUrlHandle}/${p.urlHandle}`}
                          >
                            <AppCard
                              radius="md"
                              style={{
                                width: 80,
                                height: 80,
                                padding: 0,
                                color: '#000',
                                textDecoration: 'none'
                              }}
                              withBorder
                            >
                              <AppStack
                                style={{ width: '100%', height: '100%' }}
                              >
                                <AppImage
                                  fit="contain"
                                  src={productImage?.src}
                                  style={{ width: '100%', height: '100%' }}
                                />
                              </AppStack>
                            </AppCard>
                            <AppStack style={{ gap: 0 }}>
                              <AppText
                                style={{ fontSize: 16, fontWeight: 500 }}
                              >
                                {p.name}
                              </AppText>
                              <AppText style={{ fontSize: 14, color: '#666' }}>
                                {singularPluralFormat(
                                  p.ecomVendorProductVariants.length,
                                  'variant',
                                  'variants'
                                )}
                              </AppText>
                              <AppText style={{ fontSize: 14, color: '#666' }}>
                                QTY:{' '}
                                {vendorProduct.ecomVendorBundle.ecomVendorBundleProducts.find(
                                  (f) =>
                                    f.fkEcomVendorProduct ===
                                    p.pkEcomVendorProduct
                                )?.count || 1}
                              </AppText>
                            </AppStack>
                          </AppFlexbox>
                        </AppFlexbox>
                      </AppStack>
                    </React.Fragment>
                  );
                })}
              </AppStack>
            ) : (
              <AppStack style={{}}>
                <ProductVariantInputPreview
                  ecomVendorProductInputs={
                    vendorProduct.ecomVendorProductInputs
                  }
                  ecomVendorProductVariantOptions={vendorProduct.ecomVendorProductVariants
                    .map((v) => v.ecomVendorProductVariantOptions)
                    .flat()}
                  onOptionChange={(variantOptionsSelected) => {
                    setProductFilterState({
                      ...productFilterState,
                      variantOptionsSelected
                    });
                  }}
                  selectedOptions={productFilterState.variantOptionsSelected}
                />
                <Divider />
                <AppFlexbox style={{ gap: 5, flex: 1 }}>
                  <AppText style={{ flex: 1, maxWidth: 150, color: '#666' }}>
                    Variant barcode:
                  </AppText>
                  <AppText style={{ flex: 1, fontWeight: 500 }}>
                    {selectedVariant?.barcode ? selectedVariant.barcode : 'N/A'}
                  </AppText>
                </AppFlexbox>

                <AppFlexbox style={{ gap: 5, flex: 1 }}>
                  <AppText style={{ flex: 1, maxWidth: 150, color: '#666' }}>
                    Variant SKU:
                  </AppText>
                  <AppText style={{ flex: 1, fontWeight: 500 }}>
                    {selectedVariant?.sku ? selectedVariant.sku : 'N/A'}
                  </AppText>
                </AppFlexbox>

                <AppFlexbox style={{ gap: 5, flex: 1 }}>
                  <AppText style={{ flex: 1, maxWidth: 150, color: '#666' }}>
                    Variant weight:
                  </AppText>
                  <AppText style={{ flex: 1, fontWeight: 500 }}>
                    {selectedVariant?.weight ? selectedVariant.weight : 'N/A'}
                  </AppText>
                </AppFlexbox>
              </AppStack>
            )}

            <Divider />

            <AppStack>
              {CANADA_PROVINCE_LIST_OPTIONS.every((p) =>
                vendorProvinces.some((vp) => vp.value === p.value)
              ) ? (
                <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                  Ships all across Canada
                </AppText>
              ) : (
                <Table
                  horizontalSpacing={16}
                  style={{ borderBottom: 'solid 1px  #dee2e6' }}
                  verticalSpacing={8}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '100%', padding: '8px 0px' }}>
                        <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                          Ships to
                        </AppText>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {vendorProvinces.map((p) => (
                      <Table.Tr key={p.value}>
                        <Table.Td style={{ padding: '8px 0px' }}>
                          <AppText style={{ fontSize: 16 }}>{p.label}</AppText>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </AppStack>
          </AppStack>
        </AppFlexbox>
      </AppCard>

      <AppCard radius="md" shadow="xl" withBorder>
        <Tabs defaultValue="gallery" onChange={setActiveTab} value={activeTab}>
          <Tabs.List>
            {PRODUCT_TABS_LIST.filter(
              (tab) =>
                !tab.isVisible ||
                tab.isVisible({isBundleProduct: !!vendorProduct.ecomVendorBundle})
            ).map((tab) => (
              <Tabs.Tab key={tab.value} color="dark" value={tab.value}>
                <AppText
                  style={{
                    fontSize: 16,
                    color: activeTab === tab.value ? '#000' : '#666',
                    fontWeight: activeTab === tab.value ? '500' : 'normal'
                  }}
                >
                  {tab.label}
                </AppText>
              </Tabs.Tab>
            ))}
          </Tabs.List>
          <AppStack style={{ padding: 16, paddingBottom: 0 }}>
            <Tabs.Panel value={PRODUCT_TABS_ENUM.DESCRIPTION}>
              {vendorProduct.description ? (
                <AppTextEditorContent content={vendorProduct.description} />
              ) : (
                <AppText style={{ fontWeight: 500 }}>
                  No description available for this product.
                </AppText>
              )}
            </Tabs.Panel>

            <Tabs.Panel value={PRODUCT_TABS_ENUM.DETAILS}>
              <AppFlexbox style={{ gap: 0, flex: 1 }}>
                <AppText style={{ flex: 1, maxWidth: 150, color: '#666' }}>
                  Product category:
                </AppText>
                <AppText style={{ flex: 1, fontWeight: 500 }}>
                  {vendorProduct.category ?? 'N/A'}
                </AppText>
              </AppFlexbox>

              <AppFlexbox style={{ gap: 0, flex: 1 }}>
                <AppText style={{ flex: 1, maxWidth: 150, color: '#666' }}>
                  Sub category:
                </AppText>
                <AppText style={{ flex: 1, fontWeight: 500 }}>
                  {vendorProduct.type ?? 'N/A'}
                </AppText>
              </AppFlexbox>

              <AppFlexbox style={{ gap: 0, flex: 1 }}>
                <AppText style={{ flex: 1, maxWidth: 150, color: '#666' }}>
                  Vendor:
                </AppText>
                <AppText style={{ flex: 1, fontWeight: 500 }}>
                  {vendorProduct.ecomVendor.name}
                </AppText>
              </AppFlexbox>

              <AppFlexbox style={{ gap: 0, flex: 1 }}>
                <AppText style={{ flex: 1, maxWidth: 150, color: '#666' }}>
                  Tags:
                </AppText>
                <AppText style={{ flex: 1, fontWeight: 500 }}>N/A</AppText>
              </AppFlexbox>
            </Tabs.Panel>

            <Tabs.Panel
              style={{ overflow: 'hidden', maxWidth: '100%' }}
              value={PRODUCT_TABS_ENUM.VARIANTS}
            >
              <AppStack style={{ overflow: 'auto', maxWidth: '100%' }}>
                <Table
                  horizontalSpacing={16}
                  style={{ borderBottom: 'solid 1px  #dee2e6' }}
                  verticalSpacing={8}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ padding: '8px 0px', minWidth: 200 }} />
                      <Table.Th style={{ padding: '8px 0px', minWidth: 200 }}>
                        <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                          Barcode
                        </AppText>
                      </Table.Th>
                      <Table.Th style={{ padding: '8px 0px', minWidth: 200 }}>
                        <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                          SKU
                        </AppText>
                      </Table.Th>
                      <Table.Th style={{ padding: '8px 0px', minWidth: 200 }}>
                        <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                          Price
                        </AppText>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {vendorProduct.ecomVendorProductVariants
                      .sort(
                        (a, b) =>
                          (a.ecomVendorProductVariantOptions.find(
                            (f) => f.name === variantOptions[0]?.name
                          )?.valueSort ?? 0) -
                          (b.ecomVendorProductVariantOptions.find(
                            (f) => f.name === variantOptions[0]?.name
                          )?.valueSort ?? 0)
                      )
                      .slice(
                        -MAX_PAGE_LENGTH +
                          productFilterState.variantPageIndex * MAX_PAGE_LENGTH,
                        productFilterState.variantPageIndex * MAX_PAGE_LENGTH
                      )
                      .map((v) => (
                        <Table.Tr key={v.pkEcomVendorProductVariant}>
                          <Table.Td
                            style={{ padding: '8px 0px', minWidth: 200 }}
                          >
                            <AppStack style={{ width: 50, height: 50 }}>
                              <Image
                                fit="contain"
                                h="100%"
                                src={
                                  vendorProduct.ecomVendorProductMedia.find(
                                    (m) =>
                                      m.pkEcomVendorProductMedia ===
                                      v.fkEcomVendorProductMedia
                                  )?.src
                                }
                                w="100%"
                              />
                              <AppText style={{ fontSize: 16 }}>
                                {variantOptions
                                  .map(
                                    (op) =>
                                      v.ecomVendorProductVariantOptions.find(
                                        (f) => f.name === op.name
                                      )?.value
                                  )
                                  .join(' ')}
                              </AppText>
                            </AppStack>
                          </Table.Td>
                          <Table.Td
                            style={{ padding: '8px 0px', minWidth: 200 }}
                          >
                            <AppText style={{ fontSize: 16 }}>
                              {v.barcode || 'N/A'}
                            </AppText>
                          </Table.Td>
                          <Table.Td
                            style={{ padding: '8px 0px', minWidth: 200 }}
                          >
                            <AppText style={{ fontSize: 16 }}>
                              {v.sku || 'N/A'}
                            </AppText>
                          </Table.Td>
                          <Table.Td
                            style={{ padding: '8px 0px', minWidth: 200 }}
                          >
                            <AppText style={{ fontSize: 16 }}>
                              <NumberFormatter
                                decimalScale={2}
                                fixedDecimalScale
                                prefix="$"
                                style={{ fontWeight: 700 }}
                                suffix={` ${productCurrencyType?.abbr || ''}`}
                                thousandSeparator
                                value={v.price / 100}
                              />
                            </AppText>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                  </Table.Tbody>
                </Table>
              </AppStack>
              <AppFlexbox
                style={{ gap: 5, marginTop: 10, justifyContent: 'center' }}
              >
                <Pagination
                  color="dark"
                  onChange={(index) => {
                    setProductFilterState({
                      ...productFilterState,
                      variantPageIndex: index
                    });
                  }}
                  total={Math.ceil(
                    vendorProduct.ecomVendorProductVariants.length /
                      MAX_PAGE_LENGTH
                  )}
                  value={productFilterState.variantPageIndex}
                />
              </AppFlexbox>
            </Tabs.Panel>

            <Tabs.Panel value={PRODUCT_TABS_ENUM.SHIPPING}>
              <AppStack>
                <Table
                  horizontalSpacing={16}
                  style={{ borderBottom: 'solid 1px  #dee2e6' }}
                  verticalSpacing={8}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '100%', padding: '8px 0px' }}>
                        <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                          Ships to
                        </AppText>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {vendorProvinces.map((p) => (
                      <Table.Tr key={p.value}>
                        <Table.Td style={{ padding: '8px 0px' }}>
                          <AppText style={{ fontSize: 16 }}>{p.label}</AppText>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </AppStack>
            </Tabs.Panel>
          </AppStack>
        </Tabs>
      </AppCard>
    </AppStack>
  ) : (
    <AppStack
      style={{
        flex: 1,
        width: '100%',
        maxWidth: 1600,
        alignSelf: 'center',
        gap: 0
      }}
    >
      <AppFlexbox style={{ padding: '16px 0px' }}>
        <Skeleton height={34} width={150} />
      </AppFlexbox>

      <AppCard radius="md" shadow="xl" style={{ minHeight: 600 }} withBorder>
        <AppFlexbox
          style={{
            flex: 1,
            flexDirection: isTabletOrSmaller ? 'column' : 'row'
          }}
        >
          <AppStack
            style={{
              flex: 1,
              width: '100%',
              height: '100%',
              minHeight: 350,
              maxHeight: isTabletOrSmaller ? 350 : 600,
              position: 'relative'
            }}
          >
            <Skeleton height="100%" width="100%" />
          </AppStack>

          <AppStack style={{ flex: 1 }}>
            <AppStack style={{ gap: 5, alignItems: 'start' }}>
              <Skeleton height={40} width="60%" />
              <Skeleton height={20} width={150} />
              <Skeleton height={34} width={180} />
            </AppStack>
            <Divider />

            <AppFlexbox>
              <AppStack style={{ flex: 1, gap: 5 }}>
                <Skeleton height={20} width="50%" />
                <Skeleton height={32} width="80%" />
              </AppStack>
              <AppStack style={{ flex: 1, gap: 5 }}>
                <Skeleton height={20} width="50%" />
                <Skeleton height={32} width="80%" />
              </AppStack>
              <AppStack style={{ flex: 1, gap: 5 }}>
                <Skeleton height={20} width="50%" />
                <Skeleton height={32} width="80%" />
                <Skeleton height={12} width="35%" />
              </AppStack>
            </AppFlexbox>
            <Divider />

            <AppStack style={{}}>
              {variantOptions.map((op) => (
                <Select
                  key={op.name}
                  data={op.values.map((v) => ({
                    label: v,
                    value: v
                  }))}
                  label={op.name}
                  onChange={(value) =>
                    setProductFilterState({
                      ...productFilterState,
                      variantOptionsSelected: {
                        ...productFilterState.variantOptionsSelected,
                        [op.name]: value
                      }
                    })
                  }
                  value={productFilterState.variantOptionsSelected[op.name]}
                />
              ))}

              <Skeleton height={20} width="25%" />

              <Skeleton height={20} width="25%" />

              <Skeleton height={20} width="25%" />
            </AppStack>

            <Divider />

            <AppStack>
              <Table
                horizontalSpacing={16}
                style={{ borderBottom: 'solid 1px  #dee2e6' }}
                verticalSpacing={8}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: '100%', padding: '8px 0px' }}>
                      <Skeleton height={20} width="25%" />
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td style={{ padding: '8px 0px' }}>
                      <Skeleton height={20} width="25%" />
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={{ padding: '8px 0px' }}>
                      <Skeleton height={20} width="25%" />
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </AppStack>
          </AppStack>
        </AppFlexbox>
      </AppCard>

      <AppCard radius="md" shadow="xl" withBorder>
        <Tabs>
          <Tabs.List>
            <Tabs.Tab color="dark" value="1">
              <Skeleton height={20} width={140} />
            </Tabs.Tab>
            <Tabs.Tab color="dark" value="2">
              <Skeleton height={20} width={140} />
            </Tabs.Tab>
            <Tabs.Tab color="dark" value="3">
              <Skeleton height={20} width={140} />
            </Tabs.Tab>
            <Tabs.Tab color="dark" value="1">
              <Skeleton height={20} width={140} />
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
        <AppStack style={{ marginTop: 10, gap: 10 }}>
          <Skeleton height={10} width="100%" />
          <Skeleton height={10} width="100%" />
          <Skeleton height={10} width="50%" />
        </AppStack>
      </AppCard>
    </AppStack>
  );
};

VendorCatalogProductView.propTypes = {
  baseUrl: PropTypes.string,
  vendorPreview: PropTypes.bool
};

export default VendorCatalogProductView;
